import React from "react";
import classNames from "classnames";
import { UseCases } from "@planzone/core";
import { useTranslation } from "react-i18next";
import { ExportsDataTypes, ExportsFormats } from "@planzone/dto";
import {
  useAuthentication,
  useExportData,
  useRetrieveCurrentPlanzone,
} from "@planzone/view-models";
import { useModal } from "@root/client/modal";
import { VisibilitySwitch } from "@common/visibilitySwitch";
import { MemberAvatar } from "@common/memberAvatar";
import { SecureContextualMenu } from "@common/secureContextualMenu";
import { TrashIcon } from "@common/trashIcon";
import { Props } from "./types";
import { IndicatorsList } from "./indicatorsList";
import { TextFields } from "./textFields";
import { ArrowIcon } from "./arrowIcon";
import styles from "./styles.scss";

function Report({ report: r }: Props) {
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { report, project } = r;
  const { account } = useAuthentication();
  const { isRequestPending: isExportRequestPending, exportData } =
    useExportData();
  const { pushView } = useModal();

  return (
    <VisibilitySwitch
      renderChildren={(isVisible, show, hide) => (
        <div
          className={classNames(styles.report, { [styles.opened]: isVisible })}
        >
          <div className={styles.header}>
            <div
              className={styles.headerItem}
              onClick={isVisible ? hide : show}
            >
              <div className={styles.toggle}>
                <p className={styles.name}>{report.name}</p>
                <ArrowIcon className={styles.icon} />
              </div>
            </div>
            <div className={styles.headerItem}>
              <p className={styles.submissionDate}>
                {new Date(report.submissionDate).toLocaleDateString()}
              </p>
            </div>
            <div className={styles.headerItem}>
              <div className={styles.author}>
                <div className={styles.authorAvatar}>
                  <MemberAvatar avatarUrl="" name={report.author} />
                </div>
                <p className={styles.authorName}>{report.author}</p>
              </div>
            </div>
            <div className={styles.headerItem}>
              <div className={styles.indicators}>
                <IndicatorsList
                  indicators={report.fields.filter(
                    ({ type }) => type === "indicator",
                  )}
                />
              </div>
            </div>
            <div
              className={classNames(styles.headerItem, styles.contextualMenu)}
            >
              <SecureContextualMenu
                context={{ project }}
                items={[
                  {
                    name: t("documentsPage.reportsPage.duplicate"),
                    useCase: UseCases.submitProjectReport,
                    act: () =>
                      pushView({
                        key: UseCases.submitProjectReport,
                        data: { project, initialReport: report },
                      }),
                  },
                  {
                    name: t("documentsPage.reportsPage.remove"),
                    useCase: UseCases.removeProjectReport,
                    renderIcon: (className) => (
                      <TrashIcon
                        className={classNames(className, styles.removeIcon)}
                      />
                    ),
                    color: "#db5f63",
                    act: () =>
                      pushView({
                        key: UseCases.removeProjectReport,
                        data: {
                          projectReport: report,
                          project,
                        },
                      }),
                  },
                ]}
              />
            </div>
          </div>
          {isVisible && (
            <div className={styles.drawer}>
              <div className={styles.textFields}>
                <TextFields
                  textFields={report.fields.filter(
                    ({ type }) => type === "text",
                  )}
                />
              </div>
              <div className={styles.buttonBox}>
                <button
                  className={styles.button}
                  onClick={() =>
                    !isExportRequestPending &&
                    exportData({
                      format: ExportsFormats.EXCEL,
                      author: account,
                      fileName: report.name,
                      data: {
                        type: ExportsDataTypes.PROJECT_REPORTS,
                        data: {
                          reports: [report],
                          planzone,
                          project,
                        },
                      },
                    })
                  }
                >
                  {t("documentsPage.reportsPage.export")}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    />
  );
}

export { Report };
